import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ComponentType, LazyExoticComponent } from 'react';
import { AuthProvider } from '@/context/AuthContext';
import { SidebarProvider } from '@/context/SidebarContext';
import { AdminProtectedRoute } from '@/components/common/AdminProtectedRoute';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RouteSuspense } from '@/components/common/RouteSuspense';
import { AppLayout } from '@/components/layout/AppLayout';
import { ErrorPage } from '@/pages/errors/ErrorPage';
import { Home } from '@/pages/public/Home';
import { Login } from '@/pages/auth/Login';
import { Register } from '@/pages/auth/Register';
import { ProductDetail } from '@/pages/public/ProductDetail';
import { OrderTracking } from '@/pages/public/OrderTracking';
import { OrderAccess } from '@/pages/public/OrderAccess';
import { OrderConfirmation } from '@/pages/public/OrderConfirmation';
import { About } from '@/pages/public/About';
import { Contact } from '@/pages/public/Contact';
import { Boutique } from '@/pages/public/Boutique';
import { HomeBoxProducts } from '@/pages/public/HomeBoxProducts';
import { CategoryProducts } from '@/pages/public/CategoryProducts';
import { MentionsLegalesPage, CgvPage, CgvPartenairesPage, RgpdPage, ContratReferencementPage } from '@/pages/public/LegalPages';
import { MesCommandes } from '@/pages/public/MesCommandes';
import { Profil } from '@/pages/public/Profil';
import { Panier } from '@/pages/public/Panier';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import {
  Checkout,
  Dashboard,
  Commandes,
  Clients,
  CodePromo,
  Categories,
  Transaction,
  Rapport,
  AjouterProduit,
  ListeProduits,
  GestionStock,
  AvisProduits,
  Parametres,
  AutoriteControle,
  ListeFactures,
  DetailFacture,
  CustomerBehavior,
  FAQPage,
  TestimonialsPage,
  HomeBoxes,
  HomeComingBoxes,
  UsersList,
} from '@/router/lazyPages';

const withAdmin = (Page: LazyExoticComponent<ComponentType<object>>) => (
  <AdminProtectedRoute>
    <RouteSuspense>
      <Page />
    </RouteSuspense>
  </AdminProtectedRoute>
);

const withLazy = (Page: LazyExoticComponent<ComponentType<object>>) => (
  <RouteSuspense>
    <Page />
  </RouteSuspense>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AppLayout>
        <Home />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/produit-detail',
    element: (
      <AppLayout>
        <ProductDetail />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/checkout',
    element: (
      <AppLayout>
        <RouteSuspense>
          <Checkout />
        </RouteSuspense>
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/suivi-commande',
    element: (
      <AppLayout>
        <OrderTracking />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/order-tracking',
    element: (
      <AppLayout>
        <OrderTracking />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/commande/verification',
    element: (
      <AppLayout>
        <OrderAccess />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/confirmation-commande',
    element: (
      <AppLayout>
        <OrderConfirmation />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/about',
    element: (
      <AppLayout>
        <About />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/contact',
    element: (
      <AppLayout>
        <Contact />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/boutique',
    element: (
      <AppLayout>
        <Boutique />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/home-box/:slug',
    element: (
      <AppLayout>
        <HomeBoxProducts boxType="collection" />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/home-coming-box/:slug',
    element: (
      <AppLayout>
        <HomeBoxProducts boxType="coming_soon" />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/categorie/:slug',
    element: (
      <AppLayout>
        <CategoryProducts />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/mentions-legales',
    element: (
      <AppLayout>
        <MentionsLegalesPage />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/cgv',
    element: (
      <AppLayout>
        <CgvPage />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/cgv-partenaires-marketplace',
    element: (
      <AppLayout>
        <CgvPartenairesPage />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/rgpd',
    element: (
      <AppLayout>
        <RgpdPage />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/contrat-referencement',
    element: (
      <AppLayout>
        <ContratReferencementPage />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/mes-commandes',
    element: (
      <AppLayout>
        <ProtectedRoute>
          <MesCommandes />
        </ProtectedRoute>
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/profil',
    element: (
      <AppLayout>
        <ProtectedRoute>
          <Profil />
        </ProtectedRoute>
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/panier',
    element: (
      <AppLayout>
        <Panier />
      </AppLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/login',
    element: <Login />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/register',
    element: <Register />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      { path: 'dashboard', element: withAdmin(Dashboard) },
      { path: 'commandes', element: withAdmin(Commandes) },
      { path: 'commandes-professionnelles', element: withAdmin(Commandes) },
      { path: 'clients', element: withAdmin(Clients) },
      { path: 'code-promo', element: withAdmin(CodePromo) },
      { path: 'categories', element: withAdmin(Categories) },
      { path: 'transaction', element: withAdmin(Transaction) },
      { path: 'rapport', element: withAdmin(Rapport) },
      { path: 'ajouter-produit', element: withAdmin(AjouterProduit) },
      { path: 'liste-produits', element: withAdmin(ListeProduits) },
      { path: 'gestion-stock', element: withAdmin(GestionStock) },
      { path: 'avis-produits', element: withAdmin(AvisProduits) },
      { path: 'utilisateurs', element: withAdmin(UsersList) },
      { path: 'parametres', element: withAdmin(Parametres) },
      { path: 'autorite-controle', element: withAdmin(AutoriteControle) },
      { path: 'factures', element: withAdmin(ListeFactures) },
      { path: 'factures/:id', element: withAdmin(DetailFacture) },
      { path: 'analytics/customer-behavior', element: withAdmin(CustomerBehavior) },
      { path: 'cms/faq', element: withAdmin(FAQPage) },
      { path: 'cms/testimonials', element: withAdmin(TestimonialsPage) },
      { path: 'cms/home-boxes', element: withAdmin(HomeBoxes) },
      { path: 'cms/home-coming-boxes', element: withAdmin(HomeComingBoxes) },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
    errorElement: <ErrorPage />,
  },
]);

export const AppRouter = () => {
  return (
    <AuthProvider>
      <SidebarProvider>
        <RouterProvider router={router} />
      </SidebarProvider>
    </AuthProvider>
  );
};
